import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useEnquirySources } from "@/modules/enquiries/hooks/useEnquiries";
import { useCreateApiKey } from "../hooks/useApiKeys";
import type { ApiKeyCreated } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (key: ApiKeyCreated) => void;
};

export function NewApiKeyDrawer({ isOpen, onClose, onCreated }: Props) {
  const create = useCreateApiKey();
  const { data: sources = [] } = useEnquirySources();

  const [name, setName] = useState("");
  const [source, setSource] = useState("");
  const [origins, setOrigins] = useState("");
  const [rateLimit, setRateLimit] = useState(100);
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setSource("");
      setOrigins("");
      setRateLimit(100);
      setExpiresAt("");
      setError(null);
    }
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !source) {
      setError("Name and source are required.");
      return;
    }
    try {
      const key = await create.mutateAsync({
        name: name.trim(),
        source,
        allowedOrigins: origins
          .split("\n")
          .map((o) => o.trim())
          .filter(Boolean),
        rateLimit,
        expiresAt: expiresAt || null,
      });
      onCreated(key);
      onClose();
    } catch (err) {
      setError((err as Error)?.message ?? "Failed to create key");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">New API Key</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="ak-nm">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ak-nm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="phazex.ai Production"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ak-sr">
                Source <span className="text-destructive">*</span>
              </Label>
              <Select
                id="ak-sr"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                required
              >
                <option value="">— Select source —</option>
                {sources.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Enquiries from this key are auto-tagged with this source. Add
                website-specific sources in Dropdown Manager → Enquiry Source.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ak-or">Allowed Origins (optional)</Label>
              <textarea
                id="ak-or"
                value={origins}
                onChange={(e) => setOrigins(e.target.value)}
                rows={3}
                placeholder={"https://phazex.ai\nhttps://www.phazex.ai"}
                className="w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,100%,50%)]"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              />
              <p className="text-[11px] text-muted-foreground">
                One origin per line. Leave empty to allow any origin.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ak-rl">Rate Limit (req/hour)</Label>
                <Input
                  id="ak-rl"
                  type="number"
                  min={1}
                  value={rateLimit}
                  onChange={(e) => setRateLimit(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ak-ex">Expires At (optional)</Label>
                <Input
                  id="ak-ex"
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              Generate Key
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
